import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs, { type TabEntry } from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { CreateProductionFactorsDialog } from '../-components/CreateProductionFactorsDialog';
import { ProductionFactorsTable } from '../-components/ProductionFactorsTable';
import { adminProductionFactorsQueryOptions } from '../-hooks/useAdminProductionFactors';
import { useCreateProductionFactors } from '../-hooks/useCreateProductionFactors';
import { useProductionFactorsPage } from '../-hooks/useProductionFactorsPage';
import { MAX_COUNT, type ProductionFactorScope } from '../-utils/listParams';

const SCOPES = ['by-product-type', 'by-product-level'] as const;

const productionFactorsSearch = z.object({
  /** Which tab is on screen. The old page carried the same two keys. */
  tab: fallback(z.enum(SCOPES), 'by-product-type'),
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(
    z
      .number()
      .int()
      .transform(v => Math.min(Math.max(v, 10), MAX_COUNT)),
    50,
  ),
  /**
   * `field:ASC` / `field:DESC`. Only `updatedAt` survives
   * `toProductionFactorsParams` — the endpoint maps no other column, so
   * anything else is dropped there rather than sent.
   */
  sort: z.string().optional(),
  // No `filters`: the endpoint ignores its `filter` parameter entirely, so the
  // table ships no filter controls. See `toProductionFactorsParams`.
});

export const Route = createFileRoute(
  '/management/fiber-production-factors/(pages)/',
)({
  validateSearch: productionFactorsSearch,
  loaderDeps: ({ search }) => ({
    scope: search.tab ?? 'by-product-type',
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject hands
  // the route to the router's error boundary, which replaces the page and hides
  // the table's own "could not load" banner. The component's `useQuery` hits
  // the same failure and surfaces it inline.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminProductionFactorsQueryOptions(deps))
      .catch(() => undefined),
  component: ProductionFactorsPage,
});

function ProductionFactorsPage() {
  const { getTranslation: t } = useTranslator({
    page: 'managementProductionFactors',
  });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const { tab } = Route.useSearch();
  const scope: ProductionFactorScope = tab ?? 'by-product-type';

  // Keyed on the scope so switching tabs remounts: the two tabs are separate
  // tables with their own columns, storage key and cached page, and carrying
  // one's state into the other would show the wrong rows for a frame.
  return (
    <ProductionFactorsTab
      key={scope}
      scope={scope}
      t={t}
      moreLabel={tShared('moreTabs')}
    />
  );
}

function ProductionFactorsTab({
  scope,
  t,
  moreLabel,
}: {
  scope: ProductionFactorScope;
  t: (key: string) => string;
  moreLabel: string;
}) {
  const page = useProductionFactorsPage(scope);
  const createForm = useCreateProductionFactors(page);

  const tabs: TabEntry[] = [
    { key: 'by-product-type', label: t('tabs.byProductType') },
    { key: 'by-product-level', label: t('tabs.byProductLevel') },
  ];

  return (
    // Tabs sit outside `PageContainer`: the container is the table's
    // viewport-filling frame, and putting the tab row inside it would make the
    // row scroll with the table. Same split as the permissions page.
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      {/* No padded wrapper: PageTabs already carries `pt-4 px-4` of its own
          (`min-[1600px]:pt-6 px-6`), so one around it doubled every edge. */}
      <PageTabs
        tabs={tabs}
        mode='search'
        param='tab'
        defaultTab='by-product-type'
        moreLabel={moreLabel}
        className='pt-2 min-[1600px]:pt-3'
        // Paging and sort belong to the table that set them, so they are
        // cleared on the way to the other tab rather than applied to a
        // different list.
        resetSearchOnChange
      />

      {/* The tab row already sits directly above, so the container adds no top
          padding of its own — that gap was the tabs' and the container's. */}
      <PageContainer className='pt-2 min-[1600px]:pt-3'>
        <ProductionFactorsTable page={page} onCreate={createForm.open} />
      </PageContainer>

      <CreateProductionFactorsDialog page={page} form={createForm} />
    </div>
  );
}
